import { Button, Card, Col, message, Row, Space, Table } from "antd";
import { getData } from "apiServices/common";
import { alertPop } from "apiServices/common/helper";
import CustomDatePicker from "common/CustomDatePicker";
import ListTable from "common/ListTable";
import Header from "components/Common/Header";
import moment from "moment";
import { useEffect, useState } from "react";
import "./index.css";

export default function LineWiseDefectsDHU() {
  const [filter, setFilter] = useState(null);
  const [dataSource, setDataSource] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSummary, setIsSummary] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);

  const getMaxFirstThreeValueLineId = (
    category_matrix,
    date,
    line_id,
    value_name,
  ) => {
    const lines = category_matrix?.map((category) => {
      const line = category?.values?.[date]?.find(
        (line) => line.id === line_id,
      );
      if (!line) {
        return undefined;
      }
      return {
        ...line,
        value: `${category.name}`.toLowerCase().split(" ").join("-"),
        key: `${category.id}-${category.name}`
          .toLowerCase()
          .split(" ")
          .join("-"),
      };
    });
    // remove undefined values
    const filteredLines = lines.filter((line) => line !== undefined);
    // check if every qty is 0
    const allZero = filteredLines.every((line) => line.qty === 0);
    if (allZero) {
      return "";
    }
    // find first 3 line base on max qty
    const maxLines = filteredLines.sort((a, b) => b.qty - a.qty).slice(0, 3);

    // must be one qut is greater than 0
    const isGreaterThanZero = maxLines.some((line) => line.qty > 0);
    if (!isGreaterThanZero) {
      return "";
    }

    // if index 0 and qty > 0 then return best and index 1 and qty > 0 then return better
    // if index 2 and qty > 0 then return good
    const isBest =
      maxLines[0]?.id === line_id &&
      maxLines[0]?.key === value_name &&
      maxLines[0]?.qty > 0;
    const isBetter =
      maxLines[1]?.id === line_id &&
      maxLines[1]?.key === value_name &&
      maxLines[1]?.qty > 0;
    const isGood =
      maxLines[2]?.id === line_id &&
      maxLines[2]?.key === value_name &&
      maxLines[2]?.qty > 0;
    if (isBest) {
      return "best";
    }
    if (isBetter) {
      return "better";
    }
    if (isGood) {
      return "good";
    }
    return "";
  };

  const rowTotal = (lines = [], date, value_name) => {
    switch (value_name) {
      case "total_actual_production":
        return lines.reduce(
          (acc, line) => acc + parseInt(line.total_actual_production) || 0,
          0,
        );
      case "total_endline_defects":
        return lines.reduce((acc, line) => acc + line.total_endline_defects, 0);
      case "endline_dhu":
        const total_actual_production = lines.reduce(
          (acc, line) => acc + parseInt(line.total_actual_production) || 0,
          0,
        );
        const total_endline_defects = lines.reduce(
          (acc, line) => acc + line.total_endline_defects,
          0,
        );
        const total = (total_endline_defects * 100) / total_actual_production;
        return isNaN(total) ? 0 : total.toFixed(2);
      default:
        return lines.reduce((acc, line) => acc + line.qty, 0);
    }
  };

  const values = [
    "Total actual production",
    "Total endline defects",
    "Endline dhu",
  ];

  const colSum = (data, date, line_id) => {
    let total = 0;
    Object.values(data).forEach((category) => {
      const lineKey = `${date}-${line_id}`;
      if (category[lineKey]) {
        if (!values.includes(category?.name)) {
          total += category[lineKey]?.value || 0;
        }
      }
    });

    return total;
  };

  const colTotal = (data, date) => {
    let total = 0;
    Object.values(data).forEach((value, i) => {
      if (!values.includes(value?.name)) {
        const keys = Object.keys(value).filter((key) => key.startsWith(date));
        keys.forEach((key, i) => {
          if (value[key]?.total && i === 0) {
            total += value[key]?.total || 0;
          }
        });
      }
    });
    return total;
  };

  const transform = (data) => {
    if (!data) return [];
    const category_matrix = data?.category_matrix;
    const production_summary = data?.production_summary;
    const resultCategory = [];
    const resultProduction = [];
    const allKeys = new Set();

    // create a row per category
    category_matrix?.forEach((category) => {
      const row = { name: category.name };

      // Initialize all keys with 0
      allKeys.forEach((key) => {
        row[key] = 0;
      });

      // Fill the available quantities
      Object.entries(category?.values)?.forEach(([date, lines]) => {
        lines?.forEach((line) => {
          const fiendLine = production_summary?.[date]?.find(
            (item) => item.line_id === line.id,
          );
          row[`${date}-${line.id}`] = {
            ...fiendLine,
            value: line.qty,
            total: rowTotal(lines, date, `${date}-${line.id}`),
            status: getMaxFirstThreeValueLineId(
              category_matrix,
              date,
              line.id,
              `${category.id}-${category.name}`
                .toLowerCase()
                .split(" ")
                .join("-"),
            ),
          };
        });
      });

      resultCategory.push(row);
    });

    // Fill the production summary
    ["total_actual_production", "total_endline_defects", "endline_dhu"].forEach(
      (key) => {
        const row = {
          name: key.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase()),
        };
        Object.entries(production_summary)?.forEach(([date, lines]) => {
          lines?.forEach((line) => {
            row[`${date}-${line.line_id}`] = {
              ...line,
              value: line[key],
              total: rowTotal(lines, date, key),
              status: "",
            };
          });
        });

        resultProduction.push(row);
      },
    );

    return [...resultProduction, ...resultCategory];
  };

  const transformSummary = (data) => {
    if (!data) return [];
    const category_matrix = data?.category_matrix;
    const production_summary = data?.production_summary;
    const lines = data?.lines || [];
    const result = [];

    // Helper to sum values across dates
    const sumAcrossDates = (dataArray, key) => {
      return dataArray.reduce((acc, curr) => acc + (curr[key] || 0), 0);
    };

    // Production Summary Rows
    ["total_actual_production", "total_endline_defects", "endline_dhu"].forEach(
      (key) => {
        const row = {
          name: key.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase()),
        };

        lines.forEach((line) => {
          const lineKey = `line-${line.id}`;
          const allLineData = Object.values(production_summary)
            .flat()
            .filter((item) => item.line_id === line.id);

          if (key === "endline_dhu") {
            const totalProd = production_summary
              .map((dateData) => {
                return dateData["total_actual_production"] || 0;
              })
              .reduce((acc, curr) => acc + curr, 0);
            const totalDefects = production_summary
              .map((dateData) => {
                return dateData["total_endline_defects"] || 0;
              })
              .reduce((acc, curr) => acc + curr, 0);
            const total = (totalDefects * 100) / totalProd;
            row[lineKey] = sumAcrossDates(allLineData, key);
            row["total"] = isNaN(total) ? 0 : total.toFixed(2);
          } else {
            row[lineKey] = sumAcrossDates(allLineData, key);
            row["total"] = production_summary
              .map((dateData) => {
                return dateData[key] || 0;
              })
              .reduce((acc, curr) => acc + curr, 0);
          }
        });

        result.push(row);
      },
    );

    // Category Rows
    category_matrix?.forEach((category) => {
      const row = { name: category.name, category_id: category.id };

      lines.forEach((line) => {
        const lineKey = `line-${line.id}`;
        row[lineKey] = category?.values?.find(
          (item) => item.id === line.id,
        )?.qty;
        row["total"] = category?.values?.reduce(
          (acc, item) => acc + item.qty,
          0,
        );
        row["line_id"] = line.id;
      });

      result.push(row);
    });

    return result;
  };

  const transformed = transform(dataSource);

  const transformedSummary = transformSummary(summaryData);

  const generateColumns = (data) => {
    if (!data) return [];
    const columns = [
      {
        title: "Values",
        dataIndex: "name",
        key: "name",
        width: 120,
      },
    ];

    data.category_matrix.forEach((category) => {
      Object.entries(category.values).forEach(([date, lines], i) => {
        const dateClms = {
          title: date,
          dataIndex: date,
          key: date,
          children: [],
          className: i % 2 === 0 ? "coral" : "cyan",
        };
        lines.forEach((line) => {
          const key = `${date}-${line.id}`;
          dateClms.children.push({
            title: line.line_name,
            dataIndex: [key, "value"],
            key: `${key}-value`,
            width: 120,
            className: i % 2 === 0 ? "coral" : "cyan",
            onCell: (record, i) => {
              return {
                className: record[key]?.status,
              };
            },
          });
        });
        dateClms.children.push({
          title: "Total",
          dataIndex: [date, "total"],
          key: "total",
          className: i % 2 === 0 ? "coral" : "cyan",
          width: 120,
          render: (text, record) => {
            const keys = Object.keys(record).filter((key) =>
              key.startsWith(date),
            );
            if (keys[0]) {
              return record[keys[0]]?.total;
            }
            return 0;
          },
        });
        columns.push(dateClms);
      });
    });

    // in array duplicate removal
    const uniqueColumns = columns.reduce((acc, current) => {
      const x = acc.find((item) => item.key === current.key);
      if (!x) {
        return acc.concat([current]);
      } else {
        return acc;
      }
    }, []);

    return uniqueColumns;
  };

  const generateSummaryColumns = (data) => {
    if (!data) return [];
    const lines = data?.lines || [];

    return [
      {
        title: "Value",
        dataIndex: "name",
        key: "name",
        fixed: "left",
        width: 200,
      },
      ...lines.map((line, indx) => ({
        title: line.name,
        dataIndex: `line-${line.id}`,
        key: `line-${line.id}`,
        width: 120,

        onCell: (record, i) => {
          // get max 3 value
          const maxValues = transformedSummary
            .filter((_, i) => i > 2)
            .map((item) => {
              const lineKey = `line-${line.id}`;
              return {
                value: item[lineKey],
                line_id: line.id,
                category_id: item.category_id,
              };
            });

          // check is maxValues is all 0
          const allZero = maxValues.every((item) => item.value === 0);
          if (allZero) {
            return {
              className: "summary",
            };
          }
          // sort maxValues by value
          const sortedMaxValues = maxValues.sort((a, b) => b.value - a.value);

          // get first 3 values
          const firstThreeValues = sortedMaxValues.slice(0, 3);
          // check if line_id is in firstThreeValues
          const isBest =
            firstThreeValues[0]?.category_id === record.category_id &&
            firstThreeValues[0]?.line_id === line.id;
          const isBetter =
            firstThreeValues[1]?.category_id === record.category_id &&
            firstThreeValues[1]?.line_id === line.id;
          const isGood =
            firstThreeValues[2]?.category_id === record.category_id &&
            firstThreeValues[2]?.line_id === line.id;
          if (isBest) {
            return {
              className: "best",
            };
          }
          if (isBetter) {
            return {
              className: "better",
            };
          }
          if (isGood) {
            return {
              className: "good",
            };
          }
        },
      })),
      {
        title: "Total",
        dataIndex: "total",
        key: "total",
        width: 120,
      },
    ];
  };

  // Usage
  const dynamicColumns = generateColumns(dataSource);

  const dynamicSummaryColumns = generateSummaryColumns(summaryData);

  const fetchData = async (filter) => {
    setLoading(true);
    setError(null);
    const body = {
      ...(filter?.from_date && {
        from_date: moment(filter?.from_date).format("YYYY-MM-DD"),
      }),
      ...(filter?.to_date && {
        to_date: moment(filter?.to_date).format("YYYY-MM-DD"),
      }),
    };
    try {
      const response = await getData(
        `/api/digitalization/report/dhu/line-wise-category-defects`,
        false,
        body,
      );
      if (response && response?.status === 200) {
        const masterData = response?.data?.data;
        setDataSource(masterData);
      } else {
        alertPop(
          "error",
          response?.message ? response?.message : "Something Went Wrong!",
        );
        setError("Could not load this list.");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      alertPop("error", "Failed to fetch data!");
      setError("Could not load this list.");
    } finally {
      setLoading(false);
    }
  };

  const fetchSummaryData = async (filter) => {
    setSummaryLoading(true);
    setSummaryError(null);
    const body = {
      ...(filter?.from_date && {
        from_date: moment(filter?.from_date).format("YYYY-MM-DD"),
      }),
      ...(filter?.to_date && {
        to_date: moment(filter?.to_date).format("YYYY-MM-DD"),
      }),
    };
    try {
      const response = await getData(
        `/api/digitalization/report/dhu/line-wise-category-defects-summary`, // Use a distinct endpoint for summary
        false,
        body,
      );
      if (response && response?.status === 200) {
        const masterData = response?.data?.data;
        setSummaryData(masterData); // Store in summaryData state instead of dataSource
      } else {
        alertPop(
          "error",
          response?.message ? response?.message : "Something Went Wrong!",
        );
        setSummaryError("Could not load this list.");
      }
    } catch (error) {
      console.error("Error fetching summary data:", error);
      alertPop("error", "Failed to fetch summary data!");
      setSummaryError("Could not load this list.");
    } finally {
      setSummaryLoading(false);
    }
  };

  const exportDetails = async () => {
    setLoading(true);
    const query = isSummary
      ? "/api/digitalization/report/dhu/export-line-wise-category-defects-summary"
      : `/api/digitalization/report/dhu/export-line-wise-category-defects`;
    const bodyData = {
      ...(filter?.from_date && {
        from_date: moment(filter?.from_date).format("YYYY-MM-DD"),
      }),
      ...(filter?.to_date && {
        to_date: moment(filter?.to_date).format("YYYY-MM-DD"),
      }),
    };
    try {
      const response = await getData(
        query,
        false,
        bodyData,
        { "Content-Type": "blob" },
        "arraybuffer",
      );

      if (response && response?.data) {
        // Download the file
        const outputFilename = isSummary
          ? `line-wise-defects-dhu-summary.xlsx`
          : `line-wise-defects-dhu.xlsx`;

        // Download file automatically using link attribute
        const url = URL.createObjectURL(
          new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", outputFilename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        message.success("Report Exported Successfully");
      } else {
        message.error("Something went wrong");
      }
    } catch (error) {
      console.error("Error exporting data:", error);
      message.error("Failed to export data");
    } finally {
      setLoading(false);
    }
  };

  const getRowClassName = (record) => {
    // Add class based on row content
    return record.name.toLowerCase().split(" ").join("-");
  };

  // Handle filter changes in one function
  const handleFilterChange = (type, value) => {
    setFilter((prev) => ({
      ...prev,
      [type]: value,
    }));
  };

  // Search handler
  const handleSearch = () => {
    setSummaryData(null);
    fetchData(filter);
    setIsSummary(false);
  };

  // Summary handler
  const handleSummary = () => {
    setDataSource(null);
    fetchSummaryData(filter);
    setIsSummary(true);
  };

  // Clear filters
  const clearFilters = () => {
    setFilter(null);
    fetchData(null);
    setIsSummary(false);
  };

  useEffect(() => {
    fetchData(filter);
  }, []);

  return (
    <div>
      <Card className="p-fixed">
        <Header title="Line Wise Defects DHU"></Header>
      </Card>
      <Card
        style={{
          marginTop: "20px",
        }}
      >
        <Row
          gutter={24}
          style={{
            marginTop: "20px",
          }}
        >
          <Col span={4}>
            <CustomDatePicker
              placeholder="From Date"
              style={{ width: "100%" }}
              size="small"
              value={
                filter?.from_date
                  ? moment(filter?.from_date, "YYYY-MM-DD")
                  : undefined
              }
              onChange={(date) => handleFilterChange("from_date", date)}
            />
          </Col>
          <Col span={4}>
            <CustomDatePicker
              placeholder="To Date"
              style={{ width: "100%" }}
              size="small"
              value={
                filter?.to_date
                  ? moment(filter?.to_date, "YYYY-MM-DD")
                  : undefined
              }
              onChange={(date) => handleFilterChange("to_date", date)}
            />
          </Col>
          <Col span={4}>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                size="small"
                onClick={handleSearch}
              >
                Search
              </Button>
              <Button
                type="primary"
                style={{
                  backgroundColor: "coral",
                  borderColor: "coral",
                }}
                size="small"
                onClick={handleSummary}
              >
                Summary
              </Button>
              <Button
                type="primary"
                size="small"
                onClick={exportDetails}
                style={{
                  backgroundColor: "darkcyan",
                  borderColor: "darkcyan",
                }}
              >
                Download
              </Button>

              <Button type="default" danger size="small" onClick={clearFilters}>
                Clear
              </Button>
            </Space>
          </Col>
        </Row>
        {isSummary ? (
          <ListTable
            className="LineWiseDefectsDHU"
            columns={dynamicSummaryColumns}
            dataSource={transformedSummary}
            bordered
            pagination={false}
            scroll={{
              x: "max-content",
              y: "calc(100vh - 250px)",
            }}
            loading={
              summaryLoading || transformedSummary?.length === 0 || !summaryData
            }
            error={summaryError}
            onRetry={() => fetchSummaryData(filter)}
            emptyText="No records to show yet."
            style={{
              marginTop: "20px",
            }}
            size="small"
            rowClassName={getRowClassName}
            summary={(tableData) => {
              if (tableData.length === 0) {
                return null; // No summary row if no data
              }
              const render = tableData
                .filter((_, i) => i > 2)
                ?.map((item) => item[`total`]);

              const total = render.reduce((a, b) => a + b, 0);
              return (
                <Table.Summary>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={1}>
                      Total
                    </Table.Summary.Cell>
                    {summaryData?.lines?.map((line, i) => {
                      const render = tableData
                        .filter((_, i) => i > 2)
                        ?.map((item) => item[`line-${line.id}`]);

                      const total = render.reduce((a, b) => a + b, 0);

                      return (
                        <>
                          <Table.Summary.Cell colSpan={1}>
                            {total}
                          </Table.Summary.Cell>
                        </>
                      );
                    })}
                    <Table.Summary.Cell colSpan={1}>{total}</Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        ) : null}
        {!isSummary ? (
          <ListTable
            className="LineWiseDefectsDHU"
            columns={dynamicColumns}
            dataSource={transformed}
            bordered
            pagination={false}
            scroll={{
              x: "max-content",
              y: "calc(100vh - 250px)", // Adjust based on container height
            }}
            loading={loading || transformed?.length === 0 || !dataSource}
            error={error}
            onRetry={() => fetchData(filter)}
            emptyText="No records to show yet."
            style={{
              marginTop: "20px",
            }}
            size="small"
            rowClassName={getRowClassName}
            summary={(tableData) => {
              return (
                <Table.Summary>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={1}>
                      Total
                    </Table.Summary.Cell>
                    {dataSource?.dates?.map((date, i) => {
                      const render = dataSource?.lines?.map((line) => {
                        const lineKey = `${date}-${line.id}`;
                        return (
                          <Table.Summary.Cell
                            index={i + 1}
                            colSpan={1}
                            key={lineKey}
                          >
                            {colSum(tableData, date, line.id)}
                          </Table.Summary.Cell>
                        );
                      });
                      return (
                        <>
                          {render}
                          <Table.Summary.Cell colSpan={1}>
                            {colTotal(tableData, date)}
                          </Table.Summary.Cell>
                        </>
                      );
                    })}
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        ) : null}
      </Card>
    </div>
  );
}
